import React, {
  ReactNode,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import clsx from "clsx";
import { ChevronDown, ChevronUp, CircleAlert } from "lucide-react";
import ThemeTooltip from "./UI/ThemeTooltip";

export interface AccordionItem {
  id: string | number;
  title: ReactNode;
  required?: boolean;
  subtitle?: ReactNode;
  icon?: ReactNode;
  content: ReactNode;
  tooltip?: string;
  rightContent?: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  isError?: boolean;
  disabled?: boolean;
}

interface CustomAccordionCardsProps {
  items: AccordionItem[];
  allowMultiple?: boolean;
  //  New props for controlled mode
  activeId?: string | number | null;
  onToggle?: (id: string | number, isOpen: boolean) => void;
}

// 4 custom background colors that will repeat
const CARD_BG_COLORS = [
  "#dee8ff",      // Color 1 - Light Primary
  "#d8f1de",      // Color 2 - Light Success  
  "#e3dbff",      // Color 3 - Light Warning
  "#fef8eb",         // Color 4 - Light Info
];

const COLORS = [
  "#4652b6",      // Color 1 - Light Primary
  "#449e55",      // Color 2 - Light Success  
  "#7045d1",      // Color 3 - Light Warning
  "#cb8e1b",         // Color 4 - Light Info
];

const CustomAccordionCards: React.FC<CustomAccordionCardsProps> = ({
  items,
  allowMultiple = false,
  activeId: externalActiveId,
  onToggle,
}) => {
  // Internal state for uncontrolled mode
  const [internalOpenItems, setInternalOpenItems] = useState<(string | number)[]>(() =>
    items
      .filter((item) => item.defaultOpen)
      .map((item) => item.id)
  );

  // Determine if component is controlled or uncontrolled
  const isControlled = externalActiveId !== undefined;

  // Calculate open items based on controlled/uncontrolled mode
  const openItems = useMemo(() => {
    if (isControlled) {
      // Controlled mode: activeId can be single or array based on allowMultiple
      if (allowMultiple) {
        return Array.isArray(externalActiveId) ? externalActiveId : [externalActiveId];
      } else {
        return externalActiveId ? [externalActiveId] : [];
      }
    }
    return internalOpenItems;
  }, [isControlled, externalActiveId, allowMultiple, internalOpenItems]);

  const openSet = useMemo(() => new Set(openItems), [openItems]);

  const toggleAccordion = useCallback(
    (id: string | number) => {
      if (isControlled) {
        // Controlled mode: call onToggle callback
        const isCurrentlyOpen = openSet.has(id);
        if (onToggle) {
          onToggle(id, !isCurrentlyOpen);
        }
      } else {
        // Uncontrolled mode: manage internal state
        if (allowMultiple) {
          setInternalOpenItems((prev) =>
            prev.includes(id)
              ? prev.filter((item) => item !== id)
              : [...prev, id]
          );
        } else {
          setInternalOpenItems((prev) =>
            prev.includes(id) ? [] : [id]
          );
        }
        if (onToggle) {
          const isCurrentlyOpen = openSet.has(id);
          onToggle(id, !isCurrentlyOpen);
        }
      }
    },
    [allowMultiple, isControlled, onToggle, openSet]
  );

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    id: string | number,
    disabled?: boolean
  ) => {
    if (disabled) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleAccordion(id);
    }
  };

  // Sync internal state when external activeId changes (for controlled mode)
  useEffect(() => {
    if (isControlled && externalActiveId !== undefined) {
      // No need to sync internal state in controlled mode
      return;
    }
  }, [isControlled, externalActiveId]);

  return (
    <>
      {items.map((item) => {
        const isOpen = openSet.has(item.id);
        const bgColor = '#ffffff';
        const color = "#000000";
        const hasCustomSpacing = /\b(m|mx|my|mt|mr|mb|ml)-[0-9]/.test(item.className ?? "");

        return (
          <div
            key={item.id}
            className={clsx(
              "card card-custom shadow-sm",
              !hasCustomSpacing && "mb-5",
              item.className,
            )}
            style={{ backgroundColor: bgColor }}
          >
            <div
              className={clsx(
                "d-flex justify-content-between align-items-center p-4 border-bottom",
                {
                  "cursor-pointer": !item.disabled,
                  "opacity-50": item.disabled,
                  "bg-transparent": true,
                }
              )}
              role="button"
              tabIndex={item.disabled ? -1 : 0}
              aria-expanded={isOpen}
              aria-controls={`accordion-content-${item.id}`}
              aria-disabled={item.disabled}
              onClick={() =>
                !item.disabled && toggleAccordion(item.id)
              }
              onKeyDown={(e) =>
                handleKeyDown(e, item.id, item.disabled)
              }
            >
              {/* Left */}
              <div className="d-flex align-items-center">
                {item.icon && (
                  <div className="me-3" style={{ color: color }}>
                    {item.icon}
                  </div>
                )}

                <div>
                  <h4 className="card-title mb-1 fw-bold">
                    {item.title}
                    {item.required && <span className="text-danger">*</span>}
                  </h4>

                  {item.subtitle && (
                    <div className="text-muted fs-7 mt-0">
                      {item.subtitle}
                    </div>
                  )}
                </div>
              </div>

              {/* Right */}
              <div className="d-flex align-items-center">
                {item.rightContent}
                {item.tooltip && <div className="me-2"> <ThemeTooltip text={item.tooltip} typeTooltip="icon" /></div>}
                {item.isError && <CircleAlert size={20} className="text-danger" />}

                <span className="ms-2 d-flex align-items-center">
                  {isOpen ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </span>
              </div>
            </div>

            {isOpen && (
              <div className="card-body p-4" id={`accordion-content-${item.id}`}>
                {item.content}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default React.memo(CustomAccordionCards);